import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Machine {
  Type: string;
  Name: string;
  Brand: string;
  Year: string;
  Hours: string;
  Description: string;
  Price: string;
  ShippingEstimate: string;
  ImageURL: string;
  Status: string;
}

interface MachineInquiryModalProps {
  machine: Machine | null;
  open: boolean;
  onClose: () => void;
}

const MachineInquiryModal: React.FC<MachineInquiryModalProps> = ({ machine, open, onClose }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Remove blank lines from the initial value for the message
  const initialMessage = '';

  useEffect(() => {
    if (open) {
      setName('');
      setEmail('');
      setMessage(initialMessage);
      setSubmitError('');
      setSubmitSuccess(false);
      setIsSubmitting(false);
    }
  }, [open, machine]);

  if (!open || !machine) return null;

  const prefilledMessage =
    `${t('machineInquiry.subject', 'Machine Inquiry')}:\n` +
    `${t('machineInquiry.name', 'Name')}: ${machine.Name}\n` +
    `${t('machineInquiry.brand', 'Brand')}: ${machine.Brand}\n` +
    `${t('machineInquiry.year', 'Year')}: ${machine.Year}\n` +
    `${t('machineInquiry.status', 'Status')}: ${machine.Status}\n` +
    `${t('machineInquiry.hours', 'Hours')}: ${machine.Hours || t('machineInquiry.na', 'N/A')}\n` +
    `${t('machineInquiry.price', 'Price')}: ${machine.Price}\n`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          message: message + '\n' + prefilledMessage,
        })
      });
      if (!response.ok) {
        const data = await response.json();
        setSubmitError(data.error || t('contact.errorMessage'));
        return;
      }
      setSubmitSuccess(true);
      setName('');
      setEmail('');
      setMessage('');
      setTimeout(onClose, 2000);
    } catch (err) {
      setSubmitError(t('contact.errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <form
        onSubmit={handleSubmit}
        className="bg-white dark:bg-black rounded-xl shadow-2xl p-8 max-w-md w-full flex flex-col items-center border border-gray-200 dark:border-gray-700 animate-fadeIn"
      >
        <h2 className="text-2xl font-bold mb-4 text-accent">{t('contact.title')}</h2>
        <div className="w-full mb-3">
          <label className="block text-sm font-medium mb-1">{t('contact.namePlaceholder')}</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            required
            className="w-full px-4 py-2 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-black text-black dark:text-white"
          />
        </div>
        <div className="w-full mb-3">
          <label className="block text-sm font-medium mb-1">{t('contact.emailPlaceholder')}</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            className="w-full px-4 py-2 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-black text-black dark:text-white"
          />
        </div>
        <div className="w-full mb-3">
          <label className="block text-sm font-medium mb-1">{t('contact.messagePlaceholder')}</label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={7}
            required
            className="w-full px-4 py-2 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-black text-black dark:text-white"
          />
        </div>
        <div className="w-full mb-3">
          <label className="block text-sm font-medium mb-1">{t('machineInquiry.subject')}</label>
          <pre className="bg-gray-100 dark:bg-gray-900 rounded p-3 text-sm overflow-x-auto whitespace-pre-wrap border border-gray-200 dark:border-gray-700">
            {prefilledMessage}
          </pre>
        </div>
        {submitError && <div className="text-red-500 text-sm mb-2">{submitError}</div>}
        {submitSuccess && <div className="text-green-600 text-sm mb-2">{t('contact.successMessage')}</div>}
        <div className="flex gap-2 mt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
            disabled={isSubmitting}
          >
            {t('contact.cancel', 'Cancel')}
          </button>
          <button
            type="submit"
            className="px-6 py-2 rounded bg-accent text-white font-semibold hover:bg-accent/90 transition"
            disabled={isSubmitting}
          >
            {isSubmitting ? t('contact.submitting') : t('contact.submit')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MachineInquiryModal;
